import { normalizeChamferIgnoredOptions } from './chamferPropertyDescriptor';
import { cloneChamferFeatureDefinitionData } from './cloneBlendFeatureDefinitionData';
import { convertMmToM } from '../unit';

/**
 * 倒角默认距离（米）。
 *
 * 说明：几何内部一律使用米；UI 默认值 10mm 必须经本常量进入定义字段，
 * 禁止在类型切换/PM 回退中写字面量 `10`（会被当成 10m 显示成 10000mm）。
 */
export const DEFAULT_CHAMFER_DISTANCE_M = convertMmToM(10);

/**
 * 顶点倒角默认三段距离（米）。
 */
export const DEFAULT_CHAMFER_VERTEX_DISTANCES_M: [number, number, number] = [
  DEFAULT_CHAMFER_DISTANCE_M,
  DEFAULT_CHAMFER_DISTANCE_M,
  DEFAULT_CHAMFER_DISTANCE_M,
];

/**
 * 编辑态倒角类型分组。
 *
 * 说明：
 * 1. SolidWorks 明确限制“修改过程中不能在 vertex 倒角和 edge 倒角之间互换”；
 * 2. 当前 PuppyCAD 把 face-face / offset-face / angle-distance / distance-distance 都视为非顶点倒角主链；
 * 3. 这里只负责表达编辑边界，不承担具体 UI 文案。
 */
export type ChamferEditTypeGroup = 'vertex' | 'topology';

/**
 * 解析倒角类型所属的编辑分组。
 *
 * @param type 倒角类型。
 * @returns 编辑分组。
 */
export function resolveChamferEditTypeGroup(type: ChamferType): ChamferEditTypeGroup {
  return type === 'vertex' ? 'vertex' : 'topology';
}

/**
 * 判断编辑态是否允许从当前倒角类型切换到目标类型。
 *
 * @param input 判断输入。
 * @returns 是否允许切换。
 */
export function canSwitchChamferTypeDuringEdit(input: {
  currentType: ChamferType;
  nextType: ChamferType;
}): boolean {
  return resolveChamferEditTypeGroup(input.currentType) === resolveChamferEditTypeGroup(input.nextType);
}

/**
 * 解析 Face Face 倒角的正式模式。
 *
 * 说明：
 * 1. Worker 与 web-client 都需要共享同一条默认规则；
 * 2. faceFaceMode 缺失时，才允许回退到旧字段 symmetric；
 * 3. 一旦显式指定模式，就不应再让旧字段覆盖它。
 *
 * @param definition 倒角定义。
 * @returns 归一化后的 Face Face 模式。
 */
export function resolveChamferFaceFaceMode(
  definition: Pick<ChamferFeatureDefinition, 'faceFaceMode' | 'symmetric'>,
): ChamferFaceFaceMode {
  return definition.faceFaceMode ?? (definition.symmetric === false ? 'asymmetric' : 'symmetric');
}

/**
 * 解析某类倒角允许保留的主选择项。
 *
 * @param items 原始选择项。
 * @param type 目标倒角类型。
 * @returns 归一化后的主选择项。
 */
function resolveNormalizedChamferItems(
  definition: ChamferFeatureDefinition,
  type: ChamferType,
): SelectionItemRef[] {
  const items = definition.items ?? [];
  if (type === 'vertex') {
    return items.filter((item) => item.kind === 'vertex').slice(0, 1);
  }

  if (type === 'face-face') {
    return [];
  }

  if (type === 'offset-face') {
    return resolveOffsetFaceChamferMode({
      ...definition,
      items,
    }) === 'asymmetric'
      ? items.filter((item) => item.kind === 'edge')
      : items.filter((item) => item.kind === 'edge' || item.kind === 'face');
  }

  return items.filter((item) => item.kind === 'edge' || item.kind === 'face');
}

/**
 * 判断当前选择中是否包含面。
 *
 * @param items 选择项集合。
 * @returns 是否包含面选择项。
 */
function hasFaceSelection(items: SelectionItemRef[]): boolean {
  return items.some((item) => item.kind === 'face');
}

/**
 * 解析等距面倒角的有效模式。
 *
 * 设计说明：
 * 1. SolidWorks 的等距面倒角在“对称”模式下允许边线和面参与主选择；
 * 2. “非对称”模式只能选择边线；
 * 3. 一旦主选择里存在面，模式必须被锁回对称，避免定义层保存一个 UI 不可表达的矛盾状态。
 *
 * @param definition 倒角定义。
 * @returns 等距面倒角有效模式。
 */
export function resolveOffsetFaceChamferMode(
  definition: Pick<ChamferFeatureDefinition, 'symmetric' | 'items'>,
): 'symmetric' | 'asymmetric' {
  if (hasFaceSelection(definition.items ?? [])) {
    return 'symmetric';
  }
  return definition.symmetric === false ? 'asymmetric' : 'symmetric';
}

/**
 * 归一化等距面倒角的模式状态。
 *
 * @param definition 倒角定义。
 * @returns 模式与选择项一致的倒角定义。
 */
export function normalizeOffsetFaceChamferDefinition(
  definition: ChamferFeatureDefinition,
): ChamferFeatureDefinition {
  if (definition.type !== 'offset-face') {
    return definition;
  }
  const mode = resolveOffsetFaceChamferMode(definition);
  return {
    ...definition,
    symmetric: mode !== 'asymmetric',
    items: mode === 'asymmetric'
      ? (definition.items ?? []).filter((item) => item.kind === 'edge')
      : (definition.items ?? []).filter((item) => item.kind === 'edge' || item.kind === 'face'),
  };
}

/**
 * 归一化 Face Face 倒角模式相关字段。
 *
 * @param definition 原始倒角定义。
 * @param mode 目标模式。
 * @returns 归一化后的倒角定义。
 */
export function normalizeChamferDefinitionForFaceFaceMode(
  definition: ChamferFeatureDefinition,
  mode: ChamferFaceFaceMode,
): ChamferFeatureDefinition {
  return normalizeChamferIgnoredOptions({
    ...cloneChamferFeatureDefinitionData(definition),
    faceFaceMode: mode,
    /**
     * 反转方向只对 Face Face 非对称距离有意义。
     * 对称和弦宽度模式隐藏该字段时必须同步清空，避免求解层读取到残留方向。
     */
    flipDirection: mode === 'asymmetric' ? definition.flipDirection === true : false,
    /**
     * Face Face 的真实模式语义由 faceFaceMode 决定。
     * 这里同步回写 symmetric，只是为了兼容旧字段与旧默认逻辑，
     * 避免 UI/序列化层继续保存一份和当前模式相冲突的残留状态。
     */
    symmetric: mode !== 'asymmetric',
  });
}

/**
 * 归一化倒角类型切换后的字段布局。
 *
 * @param input 归一化输入。
 * @returns 归一化后的倒角定义。
 */
export function normalizeChamferDefinitionForTypeSwitch(input: {
  definition: ChamferFeatureDefinition;
  nextType: ChamferType;
}): ChamferFeatureDefinition {
  const baseDefinition = cloneChamferFeatureDefinitionData(input.definition);
  const nextItems = resolveNormalizedChamferItems(baseDefinition, input.nextType);
  const faceFaceMode = input.nextType === 'face-face'
    ? resolveChamferFaceFaceMode(baseDefinition)
    : 'symmetric';
  const offsetFaceMode = input.nextType === 'offset-face'
    ? resolveOffsetFaceChamferMode({ ...baseDefinition, items: nextItems })
    : 'symmetric';
  const normalizedDefinition: ChamferFeatureDefinition = {
    ...baseDefinition,
    type: input.nextType,
    items: nextItems,
    faceSets: input.nextType === 'face-face' ? (baseDefinition.faceSets ?? []) : [],
    helpPoint: input.nextType === 'face-face' ? (baseDefinition.helpPoint ?? null) : null,
    partialEdgeParametersEnabled: input.nextType === 'offset-face'
      ? baseDefinition.partialEdgeParametersEnabled === true
      : false,
    partialEdges: input.nextType === 'offset-face' ? (baseDefinition.partialEdges ?? []) : [],
    flipDirection: input.nextType === 'angle-distance'
      ? baseDefinition.flipDirection === true
      : false,
    /**
     * 顶点倒角与其余倒角的参数域并不相同。
     * 切换类型时主动回收顶点专属字段，避免旧值继续污染 PropertyManager 与提交门禁。
     */
    equalDistance: input.nextType === 'vertex' ? baseDefinition.equalDistance === true : false,
    vertexDistances: input.nextType === 'vertex'
      ? (baseDefinition.vertexDistances ?? [...DEFAULT_CHAMFER_VERTEX_DISTANCES_M])
      : null,
    vertexDistanceExpressions: input.nextType === 'vertex'
      ? (baseDefinition.vertexDistanceExpressions ?? [null, null, null])
      : null,
    /**
     * Face Face 独有的宽度与模式不应残留到其它类型；
     * 否则 UI 虽然隐藏字段，命令序列化里仍会继续带着陈旧状态。
     */
    width: input.nextType === 'face-face' ? baseDefinition.width : undefined,
    widthExpression: input.nextType === 'face-face' ? (baseDefinition.widthExpression ?? null) : null,
    faceFaceMode,
    symmetric: input.nextType === 'face-face'
      ? faceFaceMode !== 'asymmetric'
      : input.nextType === 'offset-face'
        ? offsetFaceMode !== 'asymmetric'
        : input.nextType === 'distance-distance'
          ? baseDefinition.symmetric
          : true,
  };

  const normalizedForType = input.nextType === 'face-face'
    ? normalizeChamferDefinitionForFaceFaceMode(normalizedDefinition, faceFaceMode)
    : input.nextType === 'offset-face'
      ? normalizeOffsetFaceChamferDefinition(normalizedDefinition)
      : normalizedDefinition;

  return normalizeChamferIgnoredOptions(normalizedForType);
}
